import { TimestampDisplay } from '@/libs/components/src/lib/TimestampDisplay';
import { Button, ButtonColors } from '@chiroup/components/Button';
import { Input } from '@chiroup/components/form/Input';
import { Select } from '@chiroup/components/form/Select';
import { ColumnDef, DataTable } from '@chiroup/components/ui/data-table';
import { ChiroUpDayJsCommon } from '@chiroup/core/constants/stringConstants';
import { MILLISECONDS } from '@chiroup/core/constants/time';
import {
  ConnectorRunLockInfo,
  ConnectorVendorStatus,
} from '@chiroup/core/types/ConnectorAdmin.type';
import {
  ConnectorTraceDirection,
  ConnectorTraceRow,
} from '@chiroup/core/types/ConnectorTrace.type';
import { IntegrationDocumentType } from '@chiroup/core/types/IntegrationConnector.type';
import { ArrowPathIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import tz from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useClearTransmitLock,
  useConnectionApiConsole,
  useInvalidateConsole,
  usePromoteCandidate,
  useRollbackCredentials,
  useRunCredentialCheck,
  useRunTransmitBackstop,
  useSetTraceConfig,
  useStageCandidate,
  useValidateCandidate,
} from '../../hooks/useConnectionApi';
import connectionApiService from '../../services/connectionApi.service';

dayjs.extend(utc);
dayjs.extend(tz);

// ALL console timestamps on this page share one clock (defaultTimezone) and
// one shape: ISO to the millisecond. Table cells use
// <TimestampDisplay format={ISO_MS} skipCleanupDatetime />; `ts` is for inline
// (sentence-flow) spots where a block element doesn't fit.
const ISO_MS = 'YYYY-MM-DD HH:mm:ss.SSS';
const ts = (ms?: number | null) =>
  ms ? dayjs(ms).tz(ChiroUpDayJsCommon.defaultTimezone).format(ISO_MS) : '—';

const Badge = ({ on, yes, no }: { on: boolean | null | undefined; yes: string; no: string }) => (
  <span
    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
      on ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'
    }`}
  >
    {on ? yes : no}
  </span>
);

/**
 * Connection API ops console (plan §3g): service health + version, vendor
 * key status with the full rotation lifecycle, trace session control, and the
 * polling log viewer. Everything here renders connection-api endpoints; no
 * business logic lives in this file (UI is a viewer).
 */
const PageDescription = () => (
  <div className="space-y-4">
    <div className="space-y-2">
      <h3 className="font-semibold text-gray-900">Connection API operations console</h3>
      <p>
        The Connection API is ChiroUp&apos;s API-first clearinghouse integration: claims go out and
        remits/acks come back as JSON over vendor APIs (Stedi first) instead of X12 files over SFTP.
        This console is the operational surface for that service.
      </p>
      <ul className="ml-4 list-inside space-y-1">
        <li>
          <strong>Service</strong> — live health of the connection-api deployment. The version
          string changes on every deploy, so you can confirm a release actually landed.
        </li>
        <li>
          <strong>Vendor</strong> — one platform API key per vendor, held in Secrets Manager (never
          in the database; only slot <em>presence</em> is shown here). Rotation is staged: stage a
          candidate key, validate it against the vendor, promote it to active (the old key is kept
          as previous), or roll back. Every step is audited. &quot;Run credential check&quot; proves
          the active key with one cheap authenticated call — the same check a daily cron runs; only
          a true vendor rejection marks credentials invalid.
        </li>
        <li>
          <strong>Tracing</strong> — a self-expiring debug session per vendor: while active,
          connector work (submissions, polls, credential checks) writes step-by-step breadcrumbs you
          can watch below. Filters limit what gets <em>recorded</em>, not just displayed. Documents
          created while a matching session is live are stamped to keep tracing through the rest of
          their lifecycle — retries, acks, remits — even after the session ends. &quot;Trace one
          exchange&quot; stamps a single existing document the same way, with no session needed.
          Entries are PHI-free and prune after ~30 days.
        </li>
        <li>
          <strong>Unrouted queue</strong> (left nav) — inbound documents whose clinic could not be
          determined park there for a human to route or close; nothing is guessed and nothing is
          dropped.
        </li>
      </ul>
    </div>
  </div>
);

export const OpsConsole = () => {
  // ONE read paints the page (plan §3f) — every section renders from it.
  const console = useConnectionApiConsole();

  const vendorRows = console.data?.vendors ?? [];
  const [selectedVendor, setSelectedVendor] = useState<ConnectorVendorStatus | null>(null);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const active = selectedVendor ?? vendorRows[0] ?? null;

  // One selection drives both sections; problem states are flagged in the
  // option text so they're visible even when another vendor is selected.
  const vendorOptions = vendorRows.map(v => ({
    text: `${v.vendor}${v.credentialsValid === false ? ' (INVALID)' : ''}${
      !v.registered ? ' (no connector)' : ''
    }`,
    value: v.vendor,
  }));
  const selectVendor = (name: string) =>
    setSelectedVendor(vendorRows.find(v => v.vendor === name) ?? null);

  return (
    <div className="space-y-8 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Connection API</h1>
        <div className="flex items-center gap-3">
          <div
            className="cursor-pointer"
            title="About this console."
            onClick={() => setIsHelpOpen(prev => !prev)}
          >
            <QuestionMarkCircleIcon className="h-6 w-6 text-gray-500 hover:text-gray-700" />
          </div>
          <Button
            color={ButtonColors.plain}
            onClick={() => console.refetch()}
            loading={console.isFetching}
            text="Check health"
          />
        </div>
      </div>

      {isHelpOpen ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
          <PageDescription />
        </div>
      ) : null}

      {/* ── Service status (service-wide — all vendors) ────────────────── */}
      <section className="rounded-lg border p-4">
        <h2 className="mb-2 text-lg font-medium">Service</h2>
        {console.isError ? (
          <p className="text-sm text-red-600">
            Console read failed — the service is unreachable or the route is down.
          </p>
        ) : console.data ? (
          <div className="flex gap-6 text-sm">
            <span>
              status <Badge on={console.data.service.ok} yes="ok" no="down" />
            </span>
            <span>
              stage <span className="font-mono">{console.data.service.stage}</span>
            </span>
            <span>
              version <span className="font-mono">{console.data.service.version}</span>
            </span>
          </div>
        ) : (
          <p className="text-sm text-gray-500">Checking…</p>
        )}
        <PruneHealth sink={console.data?.activitySink} />
        <ServiceErrors rows={console.data?.errors.rows ?? []} />
        <ServiceWebhookRings rows={console.data?.webhooks?.rings ?? []} />
        <TransmitPanel transmit={console.data?.transmit} loading={console.isLoading} />
      </section>

      {/* ── Vendor selector — drives the vendor-specific panels below ───── */}
      {vendorOptions.length > 1 ? (
        <div className="w-56">
          <Select
            name="vendor-select"
            label="Vendor"
            options={vendorOptions}
            value={active?.vendor}
            onChange={(val: string) => selectVendor(val)}
          />
        </div>
      ) : null}

      {/* ── Vendor ─────────────────────────────────────────────────────── */}
      <section className="rounded-lg border p-4">
        {console.isLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : active ? (
          <VendorCard key={active.vendor} row={active} />
        ) : (
          <p className="text-sm text-gray-500">No platform vendors found.</p>
        )}
      </section>

      {/* ── Tracing ────────────────────────────────────────────────────── */}
      {active ? (
        <TracePanel
          vendor={active.vendor}
          integrationId={active.integrationId}
          traceConfig={console.data?.traceConfigs?.[active.vendor] ?? null}
        />
      ) : null}
    </div>
  );
};

const VendorCard = ({ row }: { row: ConnectorVendorStatus }) => {
  const [candidateKey, setCandidateKey] = useState('');
  const runCheck = useRunCredentialCheck();
  const stage = useStageCandidate();
  const validate = useValidateCandidate();
  const promote = usePromoteCandidate();
  const rollback = useRollbackCredentials();
  const args = { vendor: row.vendor, integrationId: row.integrationId };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-medium">Vendor — {row.vendor}</h2>
        <Button
          color={ButtonColors.primary}
          loading={runCheck.isLoading}
          disabled={!row.registered}
          onClick={() => runCheck.mutate(row.vendor)}
          text="Run credential check"
        />
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">integrationId {row.integrationId}</span>
          {!row.registered ? (
            <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
              no connector registered
            </span>
          ) : null}
          <Badge
            on={row.credentialsValid === true}
            yes="credentials valid"
            no={row.credentialsValid === false ? 'credentials INVALID' : 'unchecked'}
          />
          <span className="text-xs text-gray-500">checked {ts(row.credentialsLastCheckedAt)}</span>
        </div>
        <div className="flex gap-2 text-xs">
          <Badge on={row.secret.hasActive} yes="active key" no="no active key" />
          <Badge on={row.secret.hasCandidate} yes="candidate staged" no="no candidate" />
          <Badge on={row.secret.hasPrevious} yes="previous kept" no="no previous" />
        </div>
      </div>

      <div className="mt-2 text-xs text-gray-500">
        staged {ts(row.rotation.stagedAt)} by {row.rotation.stagedBy ?? '—'} · validated{' '}
        {ts(row.rotation.validatedAt)} ({row.rotation.validatedResult ?? '—'}) · promoted{' '}
        {ts(row.rotation.promotedAt)} by {row.rotation.promotedBy ?? '—'} · rolled back{' '}
        {ts(row.rotation.rolledBackAt)}
      </div>

      {!row.registered ? (
        <p className="mt-2 text-xs text-amber-700">
          This platform row has no connector implementation in code — rotation and validation are
          unavailable. If the vendor was decommissioned, close out the row; if this is unexpected, a
          registry entry is missing.
        </p>
      ) : null}

      <div className="mt-3 flex flex-wrap items-end gap-2">
        <div className="w-96">
          <Input
            name={`candidate-${row.vendor}`}
            label="Candidate API key"
            value={candidateKey}
            onChange={(val: string) => setCandidateKey(val)}
          />
        </div>
        <Button
          color={ButtonColors.plain}
          loading={stage.isLoading}
          disabled={!row.registered || !candidateKey.trim()}
          onClick={() =>
            stage.mutate(
              { ...args, apiKey: candidateKey },
              { onSuccess: () => setCandidateKey('') }
            )
          }
          text="Stage"
        />
        <Button
          color={ButtonColors.plain}
          loading={validate.isLoading}
          disabled={!row.registered || !row.secret.hasCandidate}
          onClick={() => validate.mutate(args)}
          text="Validate candidate"
        />
        <Button
          color={ButtonColors.primary}
          loading={promote.isLoading}
          disabled={!row.registered || !row.secret.hasCandidate}
          onClick={() => {
            if (window.confirm(`Promote ${row.vendor}'s candidate key to ACTIVE?`)) {
              promote.mutate(args);
            }
          }}
          text="Promote"
        />
        <Button
          color={ButtonColors.red}
          loading={rollback.isLoading}
          disabled={!row.registered || !row.secret.hasPrevious}
          onClick={() => {
            if (window.confirm(`ROLL BACK ${row.vendor} to the previous key?`)) {
              rollback.mutate(args);
            }
          }}
          text="Roll back"
        />
        {validate.data ? (
          <span className="text-sm">
            candidate: <Badge on={validate.data.valid} yes="valid" no="REJECTED" />
          </span>
        ) : null}
      </div>
    </div>
  );
};

/**
 * Dead-prune detector (Brian, 2026-07-17): the console reads the sink with NO
 * time bound — the prune cron owns retention — so the console must SAY when
 * the prune isn't doing its job. Oldest row older than retention + slack =
 * a loud red line, not a silently ballooning table.
 */
const PRUNE_SLACK_DAYS = 2;

const PruneHealth = ({ sink }: { sink?: { oldestMs: number | null; retentionDays: number } }) => {
  if (!sink?.oldestMs) return null;

  const ageDays = Math.floor((Date.now() - sink.oldestMs) / MILLISECONDS.DAY);

  if (ageDays <= sink.retentionDays + PRUNE_SLACK_DAYS) return null;

  return (
    <p className="mt-3 text-sm font-semibold text-red-700">
      Activity prune appears DEAD: the oldest sink row is {ageDays} days old (retention{' '}
      {sink.retentionDays}d). Check the startConnectorTracePrune cron.
    </p>
  );
};

/**
 * Webhook rings (plan §3g carve-out #2, INFRA-4) — the doorbell's visibility:
 * ONE row per vendor per day, `occurrences` = the day's ring count (retries
 * included; Brian, 2026-07-17). Count-first like the errors, neutral tone
 * (rings are operations, not incidents); the shared DataTable paginates.
 */
const ringColumns: ColumnDef<ConnectorTraceRow, unknown>[] = [
  {
    accessorKey: 'createdAt',
    header: 'Day (last ring)',
    cell: ({ row }) => (
      <TimestampDisplay ts={row.original.createdAt} format={ISO_MS} skipCleanupDatetime />
    ),
    enableSorting: false,
    meta: { className: 'whitespace-nowrap font-mono' },
  },
  {
    accessorKey: 'vendor',
    header: 'Vendor',
    enableSorting: false,
    meta: { className: 'whitespace-nowrap' },
  },
  {
    accessorKey: 'occurrences',
    header: 'Rings',
    cell: ({ row }) => row.original.occurrences ?? 1,
    enableSorting: false,
    meta: { align: 'right' as const, className: 'tabular-nums' },
  },
  {
    id: 'lastTransaction',
    header: 'Last transaction',
    cell: ({ row }) =>
      String((row.original.data as { lastTransactionId?: string } | null)?.lastTransactionId ?? ''),
    enableSorting: false,
    meta: { className: 'font-mono' },
  },
];

const ServiceWebhookRings = ({ rows }: { rows: ConnectorTraceRow[] }) => {
  const [open, setOpen] = useState(false);

  if (!rows.length) return null;

  const totalRings = rows.reduce((n, r) => n + (r.occurrences ?? 1), 0);
  const oldest = Math.min(...rows.map(r => r.createdAt));
  const spanDays = Math.max(1, Math.ceil((Date.now() - oldest) / MILLISECONDS.DAY));

  return (
    <div className="mt-3">
      <button
        type="button"
        className="text-sm font-medium text-gray-700 hover:underline"
        onClick={() => setOpen(prev => !prev)}
      >
        {totalRings} webhook ring{totalRings === 1 ? '' : 's'} in the last {spanDays} day
        {spanDays === 1 ? '' : 's'} {open ? '▾' : '▸'}
      </button>
      {open ? (
        <div className="mt-2 [&_td]:!align-top">
          <DataTable
            columns={ringColumns}
            data={rows}
            size="compact"
            pagination={{ pageSize: 50, pageSizeOptions: [50, 100] }}
          />
        </div>
      ) : null}
    </div>
  );
};

/**
 * Always-recorded operational errors (plan §3g carve-out) — count-first: a
 * red line only when nonzero, expanding to the shared paginated DataTable.
 */
const traceColumns: ColumnDef<ConnectorTraceRow, unknown>[] = [
  {
    accessorKey: 'createdAt',
    header: 'Time',
    cell: ({ row }) => (
      <TimestampDisplay ts={row.original.createdAt} format={ISO_MS} skipCleanupDatetime />
    ),
    enableSorting: false,
    meta: { className: 'whitespace-nowrap font-mono' },
  },
  {
    accessorKey: 'step',
    header: 'Step',
    enableSorting: false,
    meta: { className: 'whitespace-nowrap font-mono' },
  },
  { accessorKey: 'message', header: 'Message', enableSorting: false },
  {
    accessorKey: 'clinicId',
    header: 'Clinic',
    enableSorting: false,
    meta: { align: 'right' as const, className: 'font-mono' },
  },
  {
    accessorKey: 'docType',
    header: 'Doc',
    cell: ({ row }) => row.original.docType ?? '',
    enableSorting: false,
    meta: { className: 'whitespace-nowrap font-mono' },
  },
  {
    accessorKey: 'exchangeId',
    header: 'Exchange',
    cell: ({ row }) => row.original.exchangeId ?? '',
    enableSorting: false,
    meta: { className: 'whitespace-nowrap font-mono' },
  },
  {
    id: 'data',
    header: 'Data',
    cell: ({ row }) =>
      row.original.data ? (
        <details>
          <summary className="cursor-pointer text-gray-500">json</summary>
          <pre className="max-w-md overflow-auto">{JSON.stringify(row.original.data, null, 2)}</pre>
        </details>
      ) : null,
    enableSorting: false,
  },
];

const errorColumns: ColumnDef<ConnectorTraceRow, unknown>[] = [
  {
    accessorKey: 'createdAt',
    header: 'Time',
    cell: ({ row }) => (
      <TimestampDisplay ts={row.original.createdAt} format={ISO_MS} skipCleanupDatetime />
    ),
    enableSorting: false,
    meta: { className: 'whitespace-nowrap font-mono' },
  },
  {
    accessorKey: 'vendor',
    header: 'Vendor',
    enableSorting: false,
    meta: { className: 'whitespace-nowrap' },
  },
  {
    accessorKey: 'clinicId',
    header: 'Clinic',
    enableSorting: false,
    meta: { align: 'right' as const, className: 'font-mono' },
  },
  {
    accessorKey: 'exchangeId',
    header: 'Exchange',
    cell: ({ row }) => row.original.exchangeId ?? '',
    enableSorting: false,
    meta: { className: 'whitespace-nowrap font-mono' },
  },
  {
    accessorKey: 'occurrences',
    header: '#',
    cell: ({ row }) => row.original.occurrences ?? 1,
    enableSorting: false,
    meta: { align: 'right' as const, className: 'tabular-nums' },
  },
  // Only the message wraps; everything else stays on one line.
  { accessorKey: 'message', header: 'Message', enableSorting: false },
];

const ServiceErrors = ({ rows }: { rows: ConnectorTraceRow[] }) => {
  const [open, setOpen] = useState(false);

  if (!rows.length) return null;

  // All recorded errors are shown (the sink prunes at ~30 days); the label
  // reports the actual span, from the oldest row's age.
  const oldest = Math.min(...rows.map(r => r.createdAt));
  const spanDays = Math.max(1, Math.ceil((Date.now() - oldest) / MILLISECONDS.DAY));

  return (
    <div className="mt-3">
      <button
        type="button"
        className="text-sm font-medium text-red-700 hover:underline"
        onClick={() => setOpen(prev => !prev)}
      >
        {rows.length} error{rows.length === 1 ? '' : 's'} in the last {spanDays} day
        {spanDays === 1 ? '' : 's'} {open ? '▾' : '▸'}
      </button>
      {open ? (
        <div className="mt-2 [&_td]:!align-top">
          <DataTable
            columns={errorColumns}
            data={rows}
            size="compact"
            pagination={{ pageSize: 50, pageSizeOptions: [50, 100] }}
          />
        </div>
      ) : null}
    </div>
  );
};

/**
 * Pending connector transmits + the backstop's run-now (plan §3c.1a). The
 * run shares the cron's routine under the per-vendor run lock; locally it
 * transmits stuck rows INLINE — the manual local trigger.
 */
const TransmitPanel = ({
  transmit,
  loading,
}: {
  transmit?: {
    pending: Record<string, number>;
    locks: Record<string, ConnectorRunLockInfo | null>;
  };
  loading: boolean;
}) => {
  const run = useRunTransmitBackstop();
  const clearLock = useClearTransmitLock();

  const pending = transmit?.pending ?? {};
  const locks = transmit?.locks ?? {};
  const pendingTotal = Object.values(pending).reduce((a, b) => a + b, 0);
  const heldLocks = Object.entries(locks).filter(([, lock]) => lock && lock.expiresAt > Date.now());

  return (
    <div className="mt-4 border-t border-gray-950/5 pt-4">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-700">
          Transmit{' '}
          <span className={`font-normal ${pendingTotal ? 'text-amber-700' : 'text-gray-500'}`}>
            {loading ? '…' : `${pendingTotal} pending`}
          </span>
        </h3>
        <Button
          color={ButtonColors.primary}
          loading={run.isLoading}
          disabled={!pendingTotal}
          onClick={() => run.mutate()}
          text="Run transmit backstop now"
        />
      </div>

      {Object.keys(pending).length ? (
        <div className="text-sm">
          {Object.entries(pending).map(([vendor, count]) => (
            <span key={vendor} className="mr-4">
              {vendor}: <span className="font-mono">{count}</span>
            </span>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-500">No connector claims waiting to transmit.</p>
      )}

      {heldLocks.map(([vendor, lock]) => (
        <div key={vendor} className="mt-2 flex items-center gap-2 text-xs text-amber-700">
          <span>
            {vendor}: backstop running — held by <span className="font-mono">{lock!.holder}</span>{' '}
            since {ts(lock!.at)}, expires {ts(lock!.expiresAt)}
          </span>
          <Button
            color={ButtonColors.plain}
            loading={clearLock.isLoading}
            onClick={() => {
              if (
                window.confirm(`Force-clear ${vendor}'s transmit lock? Only if the holder is dead.`)
              ) {
                clearLock.mutate(vendor);
              }
            }}
            text="Clear lock"
          />
        </div>
      ))}

      {run.data ? (
        <p className="mt-2 text-xs text-gray-600">
          Last run: {run.data.reEnqueued.length} re-enqueued, {run.data.transmitted.length}{' '}
          transmitted, {run.data.failed.length} failed
          {run.data.lockedOut.length ? `, ${run.data.lockedOut.length} locked out` : ''}.
        </p>
      ) : null}
    </div>
  );
};

const DURATIONS = [
  { text: '5 minutes', value: 5 },
  { text: '15 minutes', value: 15 },
  { text: '60 minutes', value: 60 },
];

const INTERVALS = [
  { text: '5s', value: 5 },
  { text: '10s', value: 10 },
  { text: '30s', value: 30 },
  { text: 'Manual', value: 0 },
];

const DIRECTIONS = [
  { text: 'Both', value: '' },
  { text: 'Inbound', value: 'inbound' },
  { text: 'Outbound', value: 'outbound' },
];

const DOC_TYPES = [
  { text: 'All', value: '' },
  ...Object.values(IntegrationDocumentType).map(d => ({ text: d, value: d as string })),
];

const TracePanel = ({
  vendor,
  integrationId,
  traceConfig,
}: {
  vendor: string;
  integrationId: number;
  traceConfig: { config: { traceUntil: number } | null; active: boolean } | null;
}) => {
  const setConfig = useSetTraceConfig(vendor, integrationId);
  const invalidateConsole = useInvalidateConsole();

  const [duration, setDuration] = useState(15);
  const [clinicIdsCsv, setClinicIdsCsv] = useState('');
  const [intervalSec, setIntervalSec] = useState(10);
  const [paused, setPaused] = useState(false);
  const [direction, setDirection] = useState('');
  const [docType, setDocType] = useState('');
  const [rows, setRows] = useState<ConnectorTraceRow[]>([]);
  const sinceIdRef = useRef(0);

  const [stampId, setStampId] = useState('');
  const [stampDuration, setStampDuration] = useState(15);
  const [stamping, setStamping] = useState(false);
  const [stampedUntil, setStampedUntil] = useState<{ id: string; until: number } | null>(null);

  // Trace-this-one-exchange (plan §3g): stamps traceUntil on an existing
  // IntegrationExchange row — its processing traces with NO session on.
  const stampExchange = async () => {
    setStamping(true);

    try {
      const res = await connectionApiService.stampExchange(stampId.trim(), stampDuration);

      setStampedUntil({ id: res.exchangeId, until: res.traceUntil });
      setStampId('');
    } catch {
      setStampedUntil(null);
      window.alert(`No exchange '${stampId.trim()}' found to stamp.`);
    } finally {
      setStamping(false);
    }
  };

  const enable = () => {
    const clinicIds = clinicIdsCsv
      .split(',')
      .map(s => Number(s.trim()))
      .filter(Number.isInteger)
      .filter(n => n > 0);

    // Filters constrain what the session WRITES — only matching activity is
    // recorded, so the viewer is just a dumb feed of the session's rows.
    const filters = {
      ...(clinicIds.length ? { clinicIds } : {}),
      ...(docType ? { docTypes: [docType as IntegrationDocumentType] } : {}),
      ...(direction ? { direction: direction as ConnectorTraceDirection } : {}),
    };

    setConfig.mutate({
      durationMinutes: duration,
      filters: Object.keys(filters).length ? filters : undefined,
    });
  };

  const disable = () => setConfig.mutate({ durationMinutes: 0 });

  const poll = useCallback(async () => {
    const { rows: fresh } = await connectionApiService.traceLogs(vendor, integrationId, {
      sinceId: sinceIdRef.current,
      limit: 100,
    });

    if (fresh.length) {
      sinceIdRef.current = Math.max(...fresh.map(r => r.id));
      // Newest-first: fresh rows (already desc) prepend; cap memory at 500.
      setRows(prev => [...fresh, ...prev].slice(0, 500));
    }
  }, [vendor, integrationId]);

  useEffect(() => {
    // Reset the viewer when the vendor changes.
    sinceIdRef.current = 0;
    setRows([]);
  }, [vendor, integrationId]);

  const sessionActive = !!traceConfig?.active;
  const traceUntil = traceConfig?.config?.traceUntil ?? 0;

  useEffect(() => {
    // No session = nothing being written, so nothing to poll for. Manual
    // (intervalSec 0) and Pause also stop the clock; the refresh button
    // still polls on demand.
    if (!sessionActive || paused || !intervalSec) return undefined;

    const id = window.setInterval(() => {
      poll().catch(() => undefined);
    }, MILLISECONDS.inSeconds(intervalSec));

    poll().catch(() => undefined);

    return () => window.clearInterval(id);
  }, [poll, intervalSec, paused, sessionActive]);

  useEffect(() => {
    // Sessions self-expire server-side; re-read the console just past
    // traceUntil so the label flips to "off" and polling stops on its own.
    // (Codex review 2026-07-14: setConfig.reset() only cleared mutation
    // state — the expired session stayed "active" until a manual refresh.)
    if (!sessionActive || !traceUntil) return undefined;

    const id = window.setTimeout(
      () => invalidateConsole(),
      Math.max(0, traceUntil - Date.now()) + MILLISECONDS.SECOND
    );

    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionActive, traceUntil]);

  const activeUntil = useMemo(() => {
    return sessionActive ? dayjs(traceUntil).format(ISO_MS) : null;
  }, [sessionActive, traceUntil]);

  return (
    <section className="rounded-lg border p-4">
      <h2 className="mb-4 text-lg font-medium">
        Tracing — {vendor}{' '}
        {activeUntil ? (
          <span className="text-sm font-normal text-green-700">
            session active until {activeUntil}
          </span>
        ) : (
          <span className="text-sm font-normal text-gray-500">off</span>
        )}
      </h2>
      <div className="mb-4 flex items-end gap-2">
        <div className="w-40">
          <Select
            name="trace-duration"
            label="Duration"
            options={DURATIONS}
            value={duration}
            onChange={(val: number) => setDuration(val)}
          />
        </div>
        <div className="w-32">
          <Select
            name="trace-direction"
            label="Direction"
            options={DIRECTIONS}
            value={direction}
            onChange={(val: string) => setDirection(val ?? '')}
          />
        </div>
        <div className="w-32">
          <Select
            name="trace-doc-type"
            label="Doc type"
            options={DOC_TYPES}
            value={docType}
            onChange={(val: string) => setDocType(val ?? '')}
          />
        </div>
        <div className="w-56">
          <Input
            name="trace-clinics"
            label="Clinic ids (csv, optional)"
            value={clinicIdsCsv}
            onChange={(val: string) => setClinicIdsCsv(val)}
          />
        </div>
        <Button
          color={ButtonColors.primary}
          loading={setConfig.isLoading}
          onClick={enable}
          text={sessionActive ? 'Extend' : 'Start'}
        />
        <Button
          color={ButtonColors.plain}
          disabled={!sessionActive}
          onClick={disable}
          text="Stop"
        />
      </div>

      <div className="mb-4 flex items-end gap-2">
        <div className="w-72">
          <Input
            name="stamp-exchange-id"
            label="Trace one exchange (no session needed)"
            value={stampId}
            onChange={(val: string) => setStampId(val)}
          />
        </div>
        <div className="w-40">
          <Select
            name="stamp-duration"
            label=""
            options={DURATIONS}
            value={stampDuration}
            onChange={(val: number) => setStampDuration(val)}
          />
        </div>
        <Button
          color={ButtonColors.plain}
          loading={stamping}
          disabled={!stampId.trim()}
          onClick={() => stampExchange()}
          text="Trace exchange"
        />
        {stampedUntil ? (
          <span className="pb-2 text-xs text-green-700">
            <span className="font-mono">{stampedUntil.id}</span> traced until{' '}
            {ts(stampedUntil.until)}
          </span>
        ) : null}
      </div>

      <div className="mb-2 flex items-end gap-2">
        <div className="w-28">
          <Select
            name="trace-interval"
            label="Refresh"
            options={INTERVALS}
            value={intervalSec}
            onChange={(val: number) => setIntervalSec(val)}
          />
        </div>

        <Button
          color={ButtonColors.plain}
          disabled={!intervalSec}
          onClick={() => setPaused(p => !p)}
          text={paused ? 'Resume' : 'Pause'}
        />
        <Button
          color={ButtonColors.plain}
          onClick={() => {
            sinceIdRef.current = 0;
            setRows([]);
          }}
          text="Clear"
        />
        <span className="pb-2 text-xs text-gray-500">{rows.length} entries (newest first)</span>
        <div className="cursor-pointer mb-1.5" onClick={() => poll().catch(() => undefined)}>
          <ArrowPathIcon className="h-6 w-6" />
        </div>
      </div>

      {rows.length ? (
        <div className="[&_td]:!align-top">
          <DataTable
            columns={traceColumns}
            data={rows}
            size="compact"
            pagination={{ pageSize: 50, pageSizeOptions: [50, 100] }}
          />
        </div>
      ) : (
        <p className="rounded border p-3 text-xs text-gray-500">
          No trace entries yet — start a session, then exercise the pipeline (run the credential
          check, or stage/validate a key).
        </p>
      )}
    </section>
  );
};

export default OpsConsole;
